/**
 * AlertBubble — balão de alerta estilo WhatsApp.
 *
 * Exibe um alerta de chat como uma bolha com:
 * - borda lateral colorida por severidade
 * - badge de severidade + host/trigger do metadata
 * - corpo do alerta
 * - painel expansível de metadata em JSON
 * - badge "Recuperado" quando metadata.recovered === true
 * - QuotedPreview quando metadata.reply_to existe
 * - Swipe horizontal (mobile) e botão de responder (desktop) via onReply
 */
import { useRef, useState } from 'react'
import { ActionIcon, Badge, Box, Button, Group, Modal, Paper, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconChevronDown, IconChevronUp, IconEyeOff, IconMessageQuestion, IconTrash } from '@tabler/icons-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { ChatMessage } from '@/api/chat'
import { AttachmentCard } from './AttachmentCard'
import { QuotedPreview } from './QuotedPreview'
import { ReactionPills } from './ReactionPills'
import { BubbleActions } from './BubbleActions'
import { comQuebrasDuras, nomeDaIntegracao } from './integracao'
import { useDeleteMessage, useHideMessage, useSetReaction, useRemoveReaction } from '@/hooks/useChat'

// Mesmos tokens de severidade do FeedItem.tsx e ChatListRow.tsx
const SEVERITY_COLORS: Record<string, string> = {
  debug: 'gray',
  info:  'blue',
  warning: 'yellow',
  critical: 'red',
  fatal: 'grape',
}

function severityColor(severity: string | null): string {
  if (!severity) return 'gray'
  return SEVERITY_COLORS[severity] ?? 'gray'
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

interface MetadataRecord {
  recovered?: boolean
  host?: string
  trigger?: string
  [key: string]: unknown
}

function parseMeta(raw: unknown): MetadataRecord | null {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as MetadataRecord
  }
  return null
}

// Limiar de deslocamento horizontal para disparar o swipe-to-reply (px)
const SWIPE_THRESHOLD = 60

interface Props {
  cid: number
  message: ChatMessage
  onExplain?: (messageId: number) => void
  onReply?: (message: ChatMessage) => void
}

export function AlertBubble({ cid, message, onExplain, onReply }: Props) {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [expanded, setExpanded] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false)
  const [hideOpened, { open: openHide, close: closeHide }] = useDisclosure(false)
  const deleteMutation = useDeleteMessage(cid)
  const hideMutation = useHideMessage(cid)
  const setReaction = useSetReaction(cid)
  const removeReaction = useRemoveReaction(cid)

  function onToggleReaction(emoji: string) {
    if (message.my_reaction === emoji) removeReaction.mutate({ mid: message.id })
    else setReaction.mutate({ mid: message.id, emoji })
  }

  // Mensagens de alerta sem autor (sistema/autorless) podem ser apagadas
  const canDelete = message.author_user_id == null && !message.deleted
  const canHideForMe = !message.deleted

  function handleConfirmDelete() {
    deleteMutation.mutate(message.id, { onSuccess: closeDelete })
  }

  function handleConfirmHide() {
    hideMutation.mutate(message.id, { onSuccess: closeHide })
  }

  const color = severityColor(message.severity)
  const meta  = parseMeta(message.metadata)
  const isRecovered = meta?.recovered === true

  const host    = meta?.host    ? String(meta.host)    : null
  const trigger = meta?.trigger ? String(meta.trigger) : null
  const integracao = nomeDaIntegracao(meta)

  // Swipe state
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const [swipeDeltaX, setSwipeDeltaX] = useState(0)
  const swipeTriggered = useRef(false)

  function handleTouchStart(e: React.TouchEvent) {
    if (!onReply) return
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
    swipeTriggered.current = false
    setSwipeDeltaX(0)
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!onReply || touchStartX.current === null || touchStartY.current === null) return
    const dx = e.touches[0].clientX - touchStartX.current
    const dy = e.touches[0].clientY - touchStartY.current

    // Só processa se o movimento for predominantemente horizontal
    if (Math.abs(dy) > Math.abs(dx)) return

    if (dx > 0) {
      setSwipeDeltaX(Math.min(dx, SWIPE_THRESHOLD + 20))
    }
  }

  function handleTouchEnd() {
    if (!onReply) return
    if (swipeDeltaX >= SWIPE_THRESHOLD && !swipeTriggered.current) {
      swipeTriggered.current = true
      onReply(message)
    }
    setSwipeDeltaX(0)
    touchStartX.current = null
    touchStartY.current = null
  }

  const replyTo = message.metadata?.reply_to

  // Itens "Mais" do menu do BubbleActions (apagar/ocultar) — abrem os modais existentes.
  const extraItems =
    canHideForMe || canDelete ? (
      <>
        {canHideForMe && (
          <ExtraActionRow
            icon={<IconEyeOff size={14} color="var(--mantine-color-orange-6)" />}
            label="Apagar para mim"
            onClick={openHide}
          />
        )}
        {canDelete && (
          <ExtraActionRow
            icon={<IconTrash size={14} color="var(--mantine-color-red-6)" />}
            label="Apagar"
            onClick={openDelete}
          />
        )}
      </>
    ) : undefined

  return (
    <>
      {/* Modal de confirmação de exclusão */}
      <Modal
        opened={deleteOpened}
        onClose={closeDelete}
        title="Apagar mensagem"
        centered
        size="sm"
      >
        <Text size="sm">
          Tem certeza que deseja apagar esta mensagem? Esta ação não pode ser desfeita.
        </Text>
        <Group justify="flex-end" mt="md" gap="sm">
          <Button variant="subtle" color="gray" onClick={closeDelete}>
            Cancelar
          </Button>
          <Button
            color="red"
            onClick={handleConfirmDelete}
            loading={deleteMutation.isPending}
          >
            Apagar
          </Button>
        </Group>
      </Modal>

      {/* Modal de confirmação de apagar para mim */}
      <Modal
        opened={hideOpened}
        onClose={closeHide}
        title="Apagar para mim"
        centered
        size="sm"
      >
        <Text size="sm">
          Este alerta será removido apenas da sua visualização. Os outros participantes continuarão vendo-o.
        </Text>
        <Group justify="flex-end" mt="md" gap="sm">
          <Button variant="subtle" color="gray" onClick={closeHide}>
            Cancelar
          </Button>
          <Button
            color="orange"
            onClick={handleConfirmHide}
            loading={hideMutation.isPending}
          >
            Apagar para mim
          </Button>
        </Group>
      </Modal>

    <Box
      id={`msg-${message.id}`}
      style={{
        maxWidth: isMobile ? '94%' : 'min(90%, 720px)',
        width: '100%',
        position: 'relative',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <BubbleActions
        isMobile={!!isMobile}
        onReact={onToggleReaction}
        onReply={onReply ? () => onReply(message) : undefined}
        onCopy={() => navigator.clipboard.writeText(message.body ?? '')}
        onCopyJson={meta ? () => navigator.clipboard.writeText(JSON.stringify(meta, null, 2)) : undefined}
        extraItems={extraItems}
      >
        <Paper
          px="md"
          py="sm"
          withBorder
          style={{
            position: 'relative',
            minWidth: 0,
            maxWidth: '100%',
            borderLeft: `3px solid var(--mantine-color-${color}-6, var(--mantine-color-gray-6))`,
            backgroundColor: isRecovered
              ? 'var(--ai-bg-subtle)'
              : undefined,
            opacity: isRecovered ? 0.85 : 1,
            transform: swipeDeltaX > 0 ? `translateX(${swipeDeltaX}px)` : undefined,
            transition: swipeDeltaX === 0 ? 'transform 0.2s ease' : undefined,
          }}
        >
          {/* Citação — exibida no topo quando há reply_to */}
          {replyTo && <QuotedPreview replyTo={replyTo} />}

          {/* Cabeçalho: badge de severidade + host/trigger + recuperado + hora */}
          <Group gap={6} wrap="nowrap" mb={4} align="center">
            {message.severity && (
              <Badge
                size="xs"
                color={color}
                variant="light"
              >
                {message.severity}
              </Badge>
            )}

            {isRecovered && (
              <Badge size="xs" color="teal" variant="filled">
                Recuperado
              </Badge>
            )}

            {host && (
              <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace', minWidth: 0, overflowWrap: 'anywhere' }}>
                {host}
              </Text>
            )}
            {trigger && !host && (
              <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace', minWidth: 0, overflowWrap: 'anywhere' }}>
                {trigger}
              </Text>
            )}

            {integracao && !host && !trigger && (
              <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace', minWidth: 0, overflowWrap: 'anywhere' }}>
                {integracao}
              </Text>
            )}

            <Box style={{ flex: 1 }} />

            <Text size="xs" c="dimmed">
              {formatTime(message.created_at)}
            </Text>

            {/* Botão do Atendente — no cabeçalho (não sobre o scroll do metadata) */}
            {onExplain && !message.deleted && (
              <Tooltip
                label="Explicar este alerta com o @Atendente"
                withArrow
                position="top"
                openDelay={200}
              >
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="inovareTeal"
                  onClick={() => onExplain?.(message.id)}
                  aria-label="Explicar este alerta com o @Atendente"
                  style={{
                    opacity: hovered ? 1 : 0.45,
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  <IconMessageQuestion size={14} />
                </ActionIcon>
              </Tooltip>
            )}

            {/* Botão de expansão do metadata */}
            {meta && (
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => setExpanded((v) => !v)}
                aria-label={expanded ? 'Recolher metadata' : 'Expandir metadata'}
              >
                {expanded ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
              </ActionIcon>
            )}
          </Group>

          {/* Corpo — renderizado como Markdown (mesma abordagem do AssistantChat),
              para respostas do @Atendente e alertas que usam ** ## > tabelas etc. */}
          <Box
            className="assistant-markdown"
            style={{ fontSize: 'var(--mantine-font-size-sm)', overflowWrap: 'anywhere', minWidth: 0 }}
          >
            {message.kind === 'alert' || message.kind === 'system' ? (
              // Alertas usam layout multi-linha (emojis, uma info por linha).
              // Markdown padrão colapsaria os \n simples — preserva com pre-wrap.
              <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {message.body ?? ''}
              </div>
            ) : (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {integracao ? comQuebrasDuras(message.body ?? '') : (message.body ?? '')}
              </ReactMarkdown>
            )}
          </Box>

          {/* Anexos: imagens inline, outros arquivos como card */}
          {!message.deleted && message.attachments?.map((att) =>
            att.mime_type.startsWith('image/') && att.url ? (
              <img
                key={att.id}
                src={att.url}
                alt={att.filename}
                style={{ maxWidth: 260, maxHeight: 260, borderRadius: 8, cursor: 'pointer', display: 'block', marginTop: 6 }}
                onClick={() => window.open(att.url as string, '_blank', 'noopener')}
              />
            ) : (
              <AttachmentCard key={att.id} att={att} />
            )
          )}

          {/* Pills de reações */}
          {!message.deleted && (
            <ReactionPills
              reactions={message.reactions ?? []}
              myReaction={message.my_reaction}
              onToggle={onToggleReaction}
            />
          )}

          {/* Painel de metadata expansível */}
          {expanded && meta && (
            <Box
              mt="xs"
              p="xs"
              style={{
                background: 'var(--ai-bg-elevated)',
                borderRadius: 4,
                fontSize: 11,
                fontFamily: 'monospace',
                whiteSpace: 'pre',
                overflowX: 'auto',
                maxWidth: '100%',
              }}
            >
              <Stack gap={0}>
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre' }}
                >
                  {JSON.stringify(meta, null, 2)}
                </Text>
              </Stack>
            </Box>
          )}
        </Paper>
      </BubbleActions>
    </Box>
    </>
  )
}

/** Linha de ação "Mais" do menu do BubbleActions (apagar/ocultar). */
function ExtraActionRow({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 10px',
        borderRadius: 6,
        fontSize: 14,
        color: 'var(--mantine-color-text)',
      }}
      onMouseEnter={(ev) => {
        ;(ev.currentTarget as HTMLButtonElement).style.background =
          'var(--mantine-color-default-hover)'
      }}
      onMouseLeave={(ev) => {
        ;(ev.currentTarget as HTMLButtonElement).style.background = 'transparent'
      }}
    >
      <Group gap={8} wrap="nowrap">
        {icon}
        <Text size="sm">{label}</Text>
      </Group>
    </UnstyledButton>
  )
}
